import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Money } from '@heyroot/ui/primitives/Money';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import {
  fetchSuppliers,
  upsertSupplier,
  fetchSupplyRates,
  addSupplyRate,
  fetchTenantPlans,
  upsertTenantPlan,
  fetchPlatformMargin,
  queryKeys,
  type SupplierRow,
  type MessagingChannel,
  type BillingUnit,
  type PlanMode,
  type MarginLine,
  type RevenueModel,
} from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

const CHANNELS: MessagingChannel[] = ['email', 'sms', 'push'];
const BILLING_UNITS: BillingUnit[] = ['contact', 'send'];
const PLAN_MODES: PlanMode[] = ['contact', 'send'];

// "No supplier assigned" is a real, re-selectable plan state — Radix
// SelectItem can never carry value="" (check-radix-empty-select), so it needs
// a sentinel mapped back to '' at the assignedSupplierKey boundary.
const NO_SUPPLIER_SENTINEL = '__none__';

// Default the margin period to the current UTC month anchor (YYYY-MM). No
// hardcoded data — just the calendar.
function currentMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

// Parse a dollar string ("1.25") to integer cents, or null when blank ("rate
// not set"). Returns undefined on an unparseable value so the caller can flag
// it rather than silently coercing to 0. No hardcoded amounts — the operator
// types the rate.
function dollarsToCents(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  return Math.round(parseFloat(trimmed) * 100);
}

function centsToDollars(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}

function revenueModelLabel(model: RevenueModel): string {
  return model === 'flat' ? 'Flat rate' : model === 'markup' ? 'Markup' : 'Not set';
}

// ─── Suppliers + rate editor ───────────────────────────────────────────────

function RateEditor({ supplierKey, canWrite }: { supplierKey: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: rates = [], isLoading } = useQuery({
    queryKey: queryKeys.costPlusRates(supplierKey),
    queryFn: () => fetchSupplyRates(supplierKey),
  });

  const [channel, setChannel] = useState<MessagingChannel>('sms');
  const [billingUnit, setBillingUnit] = useState<BillingUnit>('send');
  const [buyRate, setBuyRate] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: (body: {
      channel: MessagingChannel;
      billingUnit: BillingUnit;
      buyRateCents: number | null;
    }) => addSupplyRate(supplierKey, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.costPlusRates(supplierKey) });
      setBuyRate('');
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to add rate.'),
  });

  function submit() {
    const cents = dollarsToCents(buyRate);
    if (cents === undefined) {
      setErr('Enter a valid amount (e.g. 0.0075 is not allowed — cents are whole).');
      return;
    }
    addMutation.mutate({ channel, billingUnit, buyRateCents: cents });
  }

  return (
    <div className="mt-3 rounded-control border border-border bg-surface-sunken p-3">
      <h4 className="text-eyebrow uppercase text-muted mb-2">
        Buy rates (Root COGS) — newest effective first
      </h4>
      {isLoading ? (
        <SkeletonTable rows={2} cols={4} />
      ) : rates.length === 0 ? (
        <p className="text-body-sm text-muted">No buy rates recorded yet.</p>
      ) : (
        <Table dense>
          <TableHead>
            <tr>
              <TableHeaderCell>Channel</TableHeaderCell>
              <TableHeaderCell>Unit</TableHeaderCell>
              <TableHeaderCell>Buy rate / unit</TableHeaderCell>
              <TableHeaderCell>Effective from</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {rates.map((rate) => (
              <TableRow key={rate.id}>
                <TableCell>{rate.channel}</TableCell>
                <TableCell>{rate.billingUnit}</TableCell>
                <TableCell className="tabular-nums">
                  <Money amountCents={rate.buyRateCents} currency={rate.currency} />
                </TableCell>
                <TableCell className="text-muted text-caption">
                  {new Date(rate.effectiveFrom).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canWrite && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-caption text-muted">Channel</span>
            <Select value={channel} onValueChange={(v) => setChannel(v as MessagingChannel)}>
              <SelectTrigger aria-label={`New rate channel for ${supplierKey}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHANNELS.map((ch) => (
                  <SelectItem key={ch} value={ch}>
                    {ch}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-caption text-muted">Unit</span>
            <Select value={billingUnit} onValueChange={(v) => setBillingUnit(v as BillingUnit)}>
              <SelectTrigger aria-label={`New rate unit for ${supplierKey}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BILLING_UNITS.map((u) => (
                  <SelectItem key={u} value={u}>
                    {u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-caption text-muted">Buy rate / unit ($)</span>
            <Input
              value={buyRate}
              onChange={(e) => setBuyRate(e.target.value)}
              placeholder="blank = not set"
              inputMode="decimal"
              className="w-36 tabular-nums"
              aria-label={`New buy rate for ${supplierKey}`}
            />
          </label>
          <Button variant="secondary" onClick={submit} disabled={addMutation.isPending}>
            {addMutation.isPending ? 'Adding…' : 'Add rate'}
          </Button>
          {err && <span className="text-body-sm text-danger-text">{err}</span>}
        </div>
      )}
    </div>
  );
}

const EMPTY_SUPPLIER = {
  supplierKey: '',
  displayName: '',
  channels: ['sms'] as MessagingChannel[],
  billingUnit: 'send' as BillingUnit,
  enabled: true,
  notes: '' as string,
};

function SuppliersSection({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: queryKeys.costPlusSuppliers,
    queryFn: fetchSuppliers,
  });

  const [expanded, setExpanded] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_SUPPLIER });
  const [formOpen, setFormOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const upsertMutation = useMutation({
    mutationFn: upsertSupplier,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.costPlusSuppliers });
      setFormOpen(false);
      setForm({ ...EMPTY_SUPPLIER });
      setEditingKey(null);
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to save supplier.'),
  });

  function openCreate() {
    setEditingKey(null);
    setForm({ ...EMPTY_SUPPLIER });
    setErr(null);
    setFormOpen(true);
  }
  function openEdit(s: SupplierRow) {
    setEditingKey(s.supplierKey);
    setForm({
      supplierKey: s.supplierKey,
      displayName: s.displayName,
      channels: s.channels,
      billingUnit: s.billingUnit,
      enabled: s.enabled,
      notes: s.notes ?? '',
    });
    setErr(null);
    setFormOpen(true);
  }

  function toggleChannel(ch: MessagingChannel) {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
    }));
  }

  function save() {
    upsertMutation.mutate({
      supplierKey: form.supplierKey,
      displayName: form.displayName,
      channels: form.channels,
      billingUnit: form.billingUnit,
      enabled: form.enabled,
      notes: form.notes.trim() === '' ? null : form.notes,
    });
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-eyebrow uppercase text-muted">Supplier registry</h2>
        {canWrite && (
          <Button variant="secondary" size="sm" onClick={openCreate}>
            + New supplier
          </Button>
        )}
      </div>

      {isLoading ? (
        <SkeletonTable rows={3} cols={5} />
      ) : suppliers.length === 0 ? (
        <EmptyState
          title="No suppliers"
          description="Add a messaging supplier to record its buy rates."
        />
      ) : (
        <Table dense>
          <TableHead>
            <tr>
              <TableHeaderCell>Supplier</TableHeaderCell>
              <TableHeaderCell>Channels</TableHeaderCell>
              <TableHeaderCell>Unit</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell />
            </tr>
          </TableHead>
          <TableBody>
            {suppliers.map((s) => (
              <Fragment key={s.supplierKey}>
                <TableRow>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-primary">{s.displayName}</span>
                      <span className="font-mono text-caption text-muted">{s.supplierKey}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-secondary">{s.channels.join(', ')}</TableCell>
                  <TableCell>{s.billingUnit}</TableCell>
                  <TableCell>
                    <Badge variant={s.enabled ? 'success' : 'neutral'}>
                      {s.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setExpanded(expanded === s.supplierKey ? null : s.supplierKey)}
                    >
                      {expanded === s.supplierKey ? 'Hide rates' : 'Rates'}
                    </Button>
                    {canWrite && (
                      <Button variant="link" size="sm" onClick={() => openEdit(s)} className="ml-2">
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {expanded === s.supplierKey && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <RateEditor supplierKey={s.supplierKey} canWrite={canWrite} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      {formOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editingKey ? 'Edit supplier' : 'New supplier'}
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div className="bg-surface rounded-control shadow-lg w-full max-w-md p-6">
            <h3 className="text-heading-3 mb-4">{editingKey ? 'Edit supplier' : 'New supplier'}</h3>
            <div className="space-y-3">
              <div>
                <span className="text-label text-secondary block mb-1">Supplier key</span>
                <Input
                  value={form.supplierKey}
                  onChange={(e) => setForm((f) => ({ ...f, supplierKey: e.target.value }))}
                  placeholder="twilio"
                  disabled={!!editingKey}
                  aria-label="Supplier key"
                />
              </div>
              <div>
                <span className="text-label text-secondary block mb-1">Display name</span>
                <Input
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="Twilio"
                  aria-label="Display name"
                />
              </div>
              <div>
                <span className="text-body-sm-emphasis block mb-1">Channels</span>
                <div className="flex gap-3">
                  {CHANNELS.map((ch) => (
                    <label key={ch} className="flex items-center gap-1.5 text-body-sm">
                      <input
                        type="checkbox"
                        checked={form.channels.includes(ch)}
                        onChange={() => toggleChannel(ch)}
                        aria-label={ch}
                      />
                      {ch}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <span className="text-label text-secondary block mb-1">Billing unit</span>
                  <Select
                    value={form.billingUnit}
                    onValueChange={(v) => setForm((f) => ({ ...f, billingUnit: v as BillingUnit }))}
                  >
                    <SelectTrigger aria-label="Billing unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILLING_UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-end gap-1.5 text-body-sm pb-2">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    aria-label="Enabled"
                  />
                  Enabled
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={save}
                disabled={
                  upsertMutation.isPending ||
                  !form.supplierKey.trim() ||
                  !form.displayName.trim() ||
                  form.channels.length === 0
                }
              >
                {upsertMutation.isPending ? 'Saving…' : 'Save supplier'}
              </Button>
            </div>
            {err && <p className="text-danger-text text-body-sm mt-2">{err}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Tenant plan editor ─────────────────────────────────────────────────────

const EMPTY_PLAN = {
  tenantId: '',
  planMode: 'send' as PlanMode,
  assignedSupplierKey: '' as string,
  sellRate: '' as string,
  markupPct: '' as string,
  enabled: true,
};

function PlansSection({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: plans = [], isLoading } = useQuery({
    queryKey: queryKeys.costPlusPlans,
    queryFn: fetchTenantPlans,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: queryKeys.costPlusSuppliers,
    queryFn: fetchSuppliers,
  });

  const [form, setForm] = useState({ ...EMPTY_PLAN });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upsertMutation = useMutation({
    mutationFn: ({
      tenantId,
      body,
    }: {
      tenantId: string;
      body: Parameters<typeof upsertTenantPlan>[1];
    }) => upsertTenantPlan(tenantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.costPlusPlans });
      setFormOpen(false);
      setForm({ ...EMPTY_PLAN });
      setEditing(false);
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed to save plan.'),
  });

  function openCreate() {
    setEditing(false);
    setForm({ ...EMPTY_PLAN });
    setErr(null);
    setFormOpen(true);
  }
  function openEdit(p: (typeof plans)[number]) {
    setEditing(true);
    setForm({
      tenantId: p.tenantId,
      planMode: p.planMode,
      assignedSupplierKey: p.assignedSupplierKey ?? '',
      sellRate: centsToDollars(p.sellRateCents),
      markupPct: p.markupPct === null ? '' : String(p.markupPct),
      enabled: p.enabled,
    });
    setErr(null);
    setFormOpen(true);
  }

  function save() {
    // Sell model is mutually exclusive: a flat sell-rate XOR a markup pct.
    // Both blank = "not configured" (margin shows as unknown, never zero).
    const sellCents = dollarsToCents(form.sellRate);
    if (sellCents === undefined) {
      setErr('Sell rate must be a valid dollar amount or blank.');
      return;
    }
    const markup = form.markupPct.trim() === '' ? null : Number(form.markupPct);
    if (markup !== null && (Number.isNaN(markup) || markup < 0)) {
      setErr('Markup % must be a non-negative number or blank.');
      return;
    }
    if (sellCents !== null && markup !== null) {
      setErr('Set EITHER a flat sell rate OR a markup %, not both.');
      return;
    }
    upsertMutation.mutate({
      tenantId: form.tenantId,
      body: {
        planMode: form.planMode,
        assignedSupplierKey:
          form.assignedSupplierKey.trim() === '' ? null : form.assignedSupplierKey,
        sellRateCents: sellCents,
        markupPct: markup,
        enabled: form.enabled,
      },
    });
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-eyebrow uppercase text-muted">Per-tenant plans</h2>
        {canWrite && (
          <Button variant="secondary" size="sm" onClick={openCreate}>
            + New plan
          </Button>
        )}
      </div>

      {isLoading ? (
        <SkeletonTable rows={3} cols={6} />
      ) : plans.length === 0 ? (
        <EmptyState
          title="No tenant plans"
          description="Assign a tenant a messaging billing plan."
        />
      ) : (
        <Table dense>
          <TableHead>
            <tr>
              <TableHeaderCell>Tenant</TableHeaderCell>
              <TableHeaderCell>Mode</TableHeaderCell>
              <TableHeaderCell>Supplier</TableHeaderCell>
              <TableHeaderCell>Sell model</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell />
            </tr>
          </TableHead>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p.tenantId}>
                <TableCell>
                  <span className="font-mono text-caption break-all">{p.tenantId}</span>
                </TableCell>
                <TableCell>{p.planMode}</TableCell>
                <TableCell className="text-secondary">
                  {p.assignedSupplierKey ?? <span className="text-muted">—</span>}
                </TableCell>
                <TableCell className="tabular-nums">
                  {p.sellRateCents !== null ? (
                    <span>
                      Flat <Money amountCents={p.sellRateCents} currency={p.currency} />
                    </span>
                  ) : p.markupPct !== null ? (
                    <span>{p.markupPct}% markup</span>
                  ) : (
                    <span className="text-muted">Not set</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={p.enabled ? 'success' : 'neutral'}>
                    {p.enabled ? 'enabled' : 'disabled'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {canWrite && (
                    <Button variant="link" size="sm" onClick={() => openEdit(p)}>
                      Edit
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {formOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editing ? 'Edit plan' : 'New plan'}
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div className="bg-surface rounded-control shadow-lg w-full max-w-md p-6">
            <h3 className="text-heading-3 mb-4">
              {editing ? 'Edit tenant plan' : 'New tenant plan'}
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-label text-secondary block mb-1">Tenant ID</span>
                <Input
                  value={form.tenantId}
                  onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
                  disabled={editing}
                  placeholder="tenant-uuid"
                  aria-label="Tenant ID"
                />
              </div>
              <div>
                <span className="text-label text-secondary block mb-1">Plan mode</span>
                <Select
                  value={form.planMode}
                  onValueChange={(v) => setForm((f) => ({ ...f, planMode: v as PlanMode }))}
                >
                  <SelectTrigger aria-label="Plan mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m === 'contact' ? 'contact (unmetered sends)' : 'send (per-send)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="text-label text-secondary block mb-1">Assigned supplier</span>
                <Select
                  value={form.assignedSupplierKey || NO_SUPPLIER_SENTINEL}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      assignedSupplierKey: v === NO_SUPPLIER_SENTINEL ? '' : v,
                    }))
                  }
                >
                  <SelectTrigger aria-label="Assigned supplier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SUPPLIER_SENTINEL}>— none —</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.supplierKey} value={s.supplierKey}>
                        {s.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <fieldset className="border border-border rounded-control p-3">
                <legend className="text-caption text-muted px-1">
                  Sell model — set ONE (flat rate or markup), or leave both blank
                </legend>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <span className="text-label text-secondary block mb-1">
                      Flat sell rate / unit ($)
                    </span>
                    <Input
                      value={form.sellRate}
                      onChange={(e) => setForm((f) => ({ ...f, sellRate: e.target.value }))}
                      placeholder="blank"
                      inputMode="decimal"
                      className="tabular-nums"
                      disabled={form.markupPct.trim() !== ''}
                      aria-label="Flat sell rate"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-label text-secondary block mb-1">Markup %</span>
                    <Input
                      value={form.markupPct}
                      onChange={(e) => setForm((f) => ({ ...f, markupPct: e.target.value }))}
                      placeholder="blank"
                      inputMode="decimal"
                      className="tabular-nums"
                      disabled={form.sellRate.trim() !== ''}
                      aria-label="Markup percent"
                    />
                  </div>
                </div>
              </fieldset>
              <label className="flex items-center gap-1.5 text-body-sm">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  aria-label="Plan enabled"
                />
                Enabled
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={save}
                disabled={upsertMutation.isPending || !form.tenantId.trim()}
              >
                {upsertMutation.isPending ? 'Saving…' : 'Save plan'}
              </Button>
            </div>
            {err && <p className="text-danger-text text-body-sm mt-2">{err}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

// ─── Margin view ────────────────────────────────────────────────────────────

function marginToneClass(cents: number | null): string {
  if (cents === null) return 'text-muted';
  if (cents > 0) return 'text-success-text';
  if (cents < 0) return 'text-danger-text';
  return 'text-primary';
}

function modelBadgeVariant(line: MarginLine): BadgeVariant {
  if (!line.hasPlan) return 'neutral';
  return line.revenueModel === 'none' ? 'warning' : 'info';
}

function MarginSection() {
  const [period, setPeriod] = useState(currentMonth());
  const [committed, setCommitted] = useState(currentMonth());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.costPlusMargin(committed),
    queryFn: () => fetchPlatformMargin(committed),
  });

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-eyebrow uppercase text-muted">Platform margin</h2>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-caption text-muted">Period (YYYY-MM)</span>
            <Input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026-06"
              className="w-32 tabular-nums"
              aria-label="Margin period"
            />
          </label>
          <Button
            variant="secondary"
            onClick={() => setCommitted(period)}
            disabled={!/^\d{4}-\d{2}$/.test(period.trim())}
          >
            Load
          </Button>
        </div>
      </div>

      {isLoading ? (
        <SkeletonTable rows={4} cols={6} />
      ) : isError ? (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
        >
          {error instanceof Error ? error.message : 'Failed to load margin.'}
        </div>
      ) : !data || data.lines.length === 0 ? (
        <EmptyState
          title="No margin lines"
          description="No tenant has a plan with usage in this period."
        />
      ) : (
        <>
          {/* Rollup summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <RollupStat label="Tenants" value={String(data.rollup.tenantCount)} />
            <RollupStat
              label="Cost"
              value={<Money amountCents={data.rollup.costCents} variant="total" />}
            />
            <RollupStat
              label="Revenue"
              value={<Money amountCents={data.rollup.revenueCents} variant="total" />}
            />
            <RollupStat
              label="Margin"
              value={
                <span className={marginToneClass(data.rollup.marginCents)}>
                  <Money amountCents={data.rollup.marginCents} variant="total" showPositiveSign />
                </span>
              }
            />
          </div>
          {data.rollup.tenantsCounted < data.rollup.tenantCount && (
            <p className="text-caption text-muted mb-3">
              {data.rollup.tenantCount - data.rollup.tenantsCounted} tenant(s) have an unknown
              margin (a rate is not set); they contribute only their known legs to the totals.
            </p>
          )}

          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>Tenant</TableHeaderCell>
                <TableHeaderCell>Model</TableHeaderCell>
                <TableHeaderCell className="text-right">Usage</TableHeaderCell>
                <TableHeaderCell className="text-right">Cost</TableHeaderCell>
                <TableHeaderCell className="text-right">Revenue</TableHeaderCell>
                <TableHeaderCell className="text-right">Margin</TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {data.lines.map((line) => (
                <TableRow key={line.tenantId}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-mono text-caption break-all">{line.tenantId}</span>
                      <span className="text-caption text-muted">
                        {line.planMode ?? '—'}
                        {line.assignedSupplierKey ? ` · ${line.assignedSupplierKey}` : ''}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={modelBadgeVariant(line)}>
                      {revenueModelLabel(line.revenueModel)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {line.usage.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Money amountCents={line.costCents} currency={line.currency} variant="muted" />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <Money amountCents={line.revenueCents} currency={line.currency} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className={marginToneClass(line.marginCents)}>
                      <Money
                        amountCents={line.marginCents}
                        currency={line.currency}
                        showPositiveSign
                      />
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </section>
  );
}

function RollupStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-control border border-border bg-surface-sunken p-3 flex flex-col gap-1">
      <span className="text-eyebrow uppercase text-muted">{label}</span>
      <span className="text-body tabular-nums">{value}</span>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CostPlusPage() {
  const { hasPerm } = useAdminAuth();
  const canWrite = hasPerm(PERMS.COST_PLUS_WRITE);

  return (
    <div className="flex flex-col gap-6 max-w-[1000px]">
      <div>
        <h1 className="text-heading-2 text-primary">Messaging billing</h1>
        <p className="text-body-sm text-muted mt-1">
          Cost-plus configuration: what Root buys (supplier COGS) vs. what each tenant is sold.
        </p>
      </div>

      <SuppliersSection canWrite={canWrite} />
      <PlansSection canWrite={canWrite} />
      <MarginSection />
    </div>
  );
}
